(function () {
  'use strict';
  if (customElements.get('leha-trust-support')) return;
  customElements.define('leha-trust-support', class extends HTMLElement {
    connectedCallback() {
      this.dispose();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('[data-trust-track]');
      this.cards = Array.from(this.querySelectorAll('[data-trust-card]'));
      this.controls = this.querySelector('[data-trust-controls]');
      this.dots = Array.from(this.querySelectorAll('[data-trust-dot]'));
      this.links = new Map(Array.from(this.querySelectorAll('[data-trust-card] a')).map(link => [link, link.getAttribute('tabindex')]));
      if (!this.track || !this.cards.length || !this.controls) return;
      this.desktop = window.matchMedia('(min-width: 768px)');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.rtl = (getComputedStyle(this).direction || this.closest('[dir]')?.getAttribute('dir')) === 'rtl';
      this.index = Math.min(this.index || 0, this.cards.length - 1);
      this.pending = null;
      this.suppressUntil = 0;
      this.querySelector('[data-trust-prev-icon]').textContent = this.rtl ? '›' : '‹';
      this.querySelector('[data-trust-next-icon]').textContent = this.rtl ? '‹' : '›';
      this.addEventListener('click', event => {
        const dot = event.target.closest('[data-trust-dot]');
        const step = event.target.closest('[data-trust-step]');
        if (dot && this.contains(dot)) this.select(this.dots.indexOf(dot));
        if (step && this.contains(step) && !step.disabled) this.select(this.index + Number(step.dataset.trustStep));
      }, { signal });
      this.track.addEventListener('click', event => {
        if (event.detail > 0 && Date.now() < this.suppressUntil && event.target.closest('a')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture:true, signal });
      this.track.addEventListener('pointerdown', event => {
        this.suppressUntil = 0;
        this.pending = null;
        this.pointer = { id:event.pointerId, x:event.clientX, y:event.clientY, swiped:false };
      }, { passive:true, signal });
      this.track.addEventListener('wheel', () => { this.pending = null; }, { passive:true, signal });
      this.track.addEventListener('pointermove', event => {
        if (!this.pointer || event.pointerId !== this.pointer.id) return;
        const dx = Math.abs(event.clientX - this.pointer.x), dy = Math.abs(event.clientY - this.pointer.y);
        if (dx > 12 && dx > dy) this.pointer.swiped = true;
      }, { passive:true, signal });
      const finishPointer = event => {
        if (!this.pointer || event.pointerId !== this.pointer.id) return;
        if (this.pointer.swiped || event.type === 'pointercancel') this.suppressUntil = Date.now() + 400;
        this.pointer = null;
      };
      this.track.addEventListener('pointerup', finishPointer, { passive:true, signal });
      this.track.addEventListener('pointercancel', finishPointer, { passive:true, signal });
      const settled = () => {
        clearTimeout(this.scrollTimer);
        const nearest = this.nearest();
        if (this.pending !== null && nearest !== this.pending) return;
        this.pending = null;
        if (!this.desktop.matches && this.isConnected) this.activate(nearest);
      };
      this.track.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(settled, 120);
      }, { passive:true, signal });
      this.track.addEventListener('scrollend', settled, { signal });
      this.addEventListener('keydown', event => {
        if (this.desktop.matches || event.target.closest('a')) return;
        if (event.target !== this.track && !event.target.closest('[data-trust-controls]')) return;
        let index;
        if (event.key === 'ArrowLeft') index = this.index + (this.rtl ? 1 : -1);
        if (event.key === 'ArrowRight') index = this.index + (this.rtl ? -1 : 1);
        if (event.key === 'Home') index = 0;
        if (event.key === 'End') index = this.cards.length - 1;
        if (index === undefined) return;
        event.preventDefault();
        this.select(index);
        if (event.target.closest('[data-trust-dot]')) this.dots[this.index].focus({ preventScroll:true });
      }, { signal });
      this.addEventListener('shopify:block:select', event => {
        const index = this.cards.findIndex(card => card.dataset.trustCard === event.detail?.blockId);
        if (index >= 0) this.select(index, false);
      }, { signal });
      this.desktop.addEventListener('change', () => this.layout(), { signal });
      this.motion.addEventListener('change', () => { if (this.pending !== null) this.select(this.index, false); }, { signal });
      if (window.ResizeObserver) {
        let width = this.track.clientWidth;
        this.observer = new ResizeObserver(() => {
          const next = this.track.clientWidth;
          if (next !== width) { width = next; this.layout(); }
        });
        this.observer.observe(this.track);
      }
      this.layout();
    }
    layout() {
      clearTimeout(this.scrollTimer);
      this.pending = null;
      this.controls.hidden = this.desktop.matches || this.cards.length < 2;
      this.track.tabIndex = this.desktop.matches ? -1 : 0;
      this.dataset.mode = this.desktop.matches ? 'desktop' : 'mobile';
      this.activate(this.index);
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        if (!this.controller.signal.aborted && this.isConnected && !this.desktop.matches) this.select(this.index, false);
      });
    }
    nearest() {
      const track = this.track.getBoundingClientRect();
      let index = 0, distance = Infinity;
      this.cards.forEach((card, current) => {
        const rect = card.getBoundingClientRect();
        const delta = Math.abs(this.rtl ? rect.right - track.right : rect.left - track.left);
        if (delta < distance) { distance = delta; index = current; }
      });
      return index;
    }
    select(index, animate = true) {
      index = Math.max(0, Math.min(index, this.cards.length - 1));
      this.pending = null;
      this.activate(index);
      if (this.desktop.matches) return;
      const track = this.track.getBoundingClientRect(), card = this.cards[index].getBoundingClientRect();
      const padding = 4;
      const delta = this.rtl ? card.right - track.right + padding : card.left - track.left - padding;
      if (animate && !this.motion.matches && Math.abs(delta) > 1) this.pending = index;
      this.track.scrollTo({ left:this.track.scrollLeft + delta, behavior:animate && !this.motion.matches ? 'smooth' : 'auto' });
    }
    activate(index) {
      this.index = index;
      const focused = document.activeElement;
      if (!this.desktop.matches && this.cards.some((card, i) => i !== index && card.contains(focused))) this.track.focus({ preventScroll:true });
      this.cards.forEach((card, i) => {
        const offscreen = !this.desktop.matches && i !== index;
        card.inert = offscreen;
        if (offscreen) card.setAttribute('aria-hidden', 'true'); else card.removeAttribute('aria-hidden');
        card.querySelectorAll('a').forEach(link => {
          const original = this.links.get(link);
          if (offscreen) link.tabIndex = -1;
          else if (original === null) link.removeAttribute('tabindex'); else link.setAttribute('tabindex', original);
        });
      });
      this.dots.forEach((dot, i) => { if (i === index) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
      this.querySelector('[data-trust-step="-1"]').disabled = index === 0;
      this.querySelector('[data-trust-step="1"]').disabled = index === this.cards.length - 1;
      this.querySelector('[data-trust-status]').textContent = this.dataset.positionLabel.replace('__INDEX__', String(index + 1));
    }
    dispose() {
      this.controller?.abort();
      this.observer?.disconnect();
      clearTimeout(this.scrollTimer);
      cancelAnimationFrame(this.frame);
      this.cards?.forEach(card => { card.inert = false; card.removeAttribute('aria-hidden'); });
      this.links?.forEach((value, link) => { if (value === null) link.removeAttribute('tabindex'); else link.setAttribute('tabindex', value); });
      if (this.controls) this.controls.hidden = true;
      if (this.track) this.track.tabIndex = 0;
      this.pointer = null;
      this.pending = null;
    }
    disconnectedCallback() { this.dispose(); }
  });
})();
